import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { useTranslate } from "@similarweb/i18n-provider";
import { formatDate, fullMonthAndYear, Table } from "@similarweb/pro-logic-components";
import { Flex } from "@similarweb/ui-components/foundations";

import {
    useCategoriesComparison,
    type ICategoryComparison,
} from "app/pages/industry-analysis/categories-context/CategoriesContext";
import CountryService from "app/services/CountryService";
import DurationService, { BasicDurations } from "app/services/DurationService";

import { CONVERSION_RATE_TRANSLATION_KEYS } from "../../consts";
import { ConversionRateCardShell } from "../../shared/ConversionRateCardShell";
import { ConversionRateSectionHeader } from "../../shared/ConversionRateSectionHeader";
import { resolveCardState } from "../../shared/resolveCardState";
import { COMPARISON_AUTOMATION_IDS } from "../automationIds";
import { buildComparisonRows } from "../buildComparisonRows";

import { getComparisonOverviewColumns } from "./getComparisonOverviewColumns";
import { useConversionRateComparisonOverviewData } from "./useConversionRateComparisonOverviewData";

interface IConversionRateComparisonOverviewProps {
    categories: ICategoryComparison[];
    availabilityState: "available" | "locked";
}

const LOADER_ROW_COUNT = 2;

export const ConversionRateComparisonOverview = ({
    categories,
    availabilityState,
}: IConversionRateComparisonOverviewProps) => {
    const { translate } = useTranslate();
    const { params } = useCurrentStateAndParams();
    const { hasSelectedCategoryWithSegments } = useCategoriesComparison();

    const country = params.country as number;
    const includeSubdomains = (params.includeSubdomains as boolean | undefined) ?? true;
    const durationParam = (params.duration as string) ?? BasicDurations.LAST_MONTH;
    const {
        iso: { from, to },
        isWindow,
    } = DurationService.getDurationData(durationParam, params.comparedDuration as string).forAPI;

    const sharedParams = { country, includeSubdomains, from, to, isWindow };

    const { data, isLoading } = useConversionRateComparisonOverviewData(categories, sharedParams, {
        enabled: availabilityState === "available",
        hasSelectedCategoryWithSegments,
    });

    const countryText = CountryService.getCountryById(country)?.text ?? "";
    const dateRangeText = formatDate(from, to, fullMonthAndYear);
    const cardState = resolveCardState(availabilityState, isLoading);

    const rows = useMemo(() => buildComparisonRows(categories, data), [categories, data]);
    const columnConfig = useMemo(
        () => getComparisonOverviewColumns(translate, COMPARISON_AUTOMATION_IDS.card),
        [translate],
    );

    if (cardState === "available" && rows.every((row) => !row.entry)) {
        return null;
    }

    return (
        <Flex
            flexDirection="column"
            gap="16px"
            margin="8px 0 0 0"
            data-automation={COMPARISON_AUTOMATION_IDS.section}
        >
            <ConversionRateSectionHeader isComparison />
            <ConversionRateCardShell
                title={translate(CONVERSION_RATE_TRANSLATION_KEYS.comparisonCardTitle)}
                tooltip={translate(CONVERSION_RATE_TRANSLATION_KEYS.comparisonCardTooltip)}
                dateRangeText={dateRangeText}
                countryCode={country}
                countryText={countryText}
                state={cardState}
                cardAutomationId={COMPARISON_AUTOMATION_IDS.card}
                lockedCtaAutomationId={COMPARISON_AUTOMATION_IDS.lockedCta}
                loadingRowCount={LOADER_ROW_COUNT}
            >
                <Table
                    theme="white"
                    tableLayout="auto"
                    sticky={false}
                    data={rows}
                    columnConfig={columnConfig}
                    bordersConfig={{ body: false, header: false }}
                    rowKey={(row) => row.id}
                    data-automation={COMPARISON_AUTOMATION_IDS.table}
                />
            </ConversionRateCardShell>
        </Flex>
    );
};
